import { Globe, Check, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';

const languages = [
    { code: 'pt-BR', label: 'Português', flag: '🇧🇷' },
    { code: 'en', label: 'English', flag: '🇺🇸' },
    { code: 'es', label: 'Español', flag: '🇪🇸' },
    { code: 'it', label: 'Italiano', flag: '🇮🇹' },
    { code: 'fr', label: 'Français', flag: '🇫🇷' },
];

function BottomSheet({
    isOpen,
    onClose,
    currentLang,
    onSelect,
}: {
    isOpen: boolean;
    onClose: () => void;
    currentLang: string;
    onSelect: (code: string) => void;
}) {
    // Lock body scroll when open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    return createPortal(
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100]"
                        onClick={onClose}
                    />

                    {/* Sheet */}
                    <motion.div
                        initial={{ y: '100%' }}
                        animate={{ y: 0 }}
                        exit={{ y: '100%' }}
                        transition={{ type: 'spring', damping: 30, stiffness: 350 }}
                        className="fixed bottom-0 left-0 right-0 z-[101]
                            bg-gradient-to-b from-[#FDF8F3] to-[#FAF5EF]
                            rounded-t-3xl shadow-2xl
                            max-h-[60vh] overflow-hidden"
                    >
                        {/* Handle bar */}
                        <div className="flex justify-center pt-3 pb-1">
                            <div className="w-10 h-1 rounded-full bg-[#C8A27A]/30" />
                        </div>

                        {/* Header */}
                        <div className="flex items-center justify-between px-6 py-3">
                            <h3 className="text-base font-semibold text-[#6F4E37]">
                                <Globe className="inline-block h-4 w-4 mr-2 -mt-0.5" />
                                Idioma
                            </h3>
                            <button
                                onClick={onClose}
                                className="p-1.5 rounded-full hover:bg-[#6F4E37]/10 transition-colors"
                            >
                                <X className="h-4 w-4 text-[#6F4E37]/60" />
                            </button>
                        </div>

                        {/* Language list */}
                        <div className="px-4 pb-8 space-y-1.5">
                            {languages.map((lang) => {
                                const isActive = currentLang === lang.code;
                                return (
                                    <button
                                        key={lang.code}
                                        onClick={() => onSelect(lang.code)}
                                        className={`flex w-full items-center justify-between
                                            rounded-2xl px-4 py-3.5 text-[15px] font-medium
                                            transition-all duration-200 active:scale-[0.98]
                                            ${isActive
                                                ? 'bg-[#6F4E37] text-white shadow-md shadow-[#6F4E37]/20'
                                                : 'bg-white/60 text-[#6F4E37] border border-[#C8A27A]/15 hover:bg-white hover:border-[#C8A27A]/30'
                                            }`}
                                    >
                                        <span className="flex items-center gap-3">
                                            <span className="text-lg">{lang.flag}</span>
                                            <span>{lang.label}</span>
                                        </span>
                                        {isActive && <Check className="h-4 w-4" />}
                                    </button>
                                );
                            })}
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>,
        document.body
    );
}

export function LanguageSelector() {
    const { i18n } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    const checkMobile = useCallback(() => {
        setIsMobile(window.innerWidth < 768);
    }, []);

    useEffect(() => {
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, [checkMobile]);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const currentLang = languages.find((l) => l.code === i18n.language) || languages[0];

    const handleSelect = (code: string) => {
        i18n.changeLanguage(code);
        setIsOpen(false);
    };

    return (
        <div className="relative" ref={ref}>
            <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsOpen(!isOpen)}
                className="gap-2 rounded-full border border-border/50 bg-background/50 backdrop-blur-sm hover:bg-background/80"
            >
                <Globe className="h-4 w-4" />
                <span className="hidden sm:inline-block">{currentLang.label}</span>
                <span className="sm:hidden">{currentLang.flag}</span>
            </Button>

            {/* Mobile: Bottom Sheet */}
            {isMobile && (
                <BottomSheet
                    isOpen={isOpen}
                    onClose={() => setIsOpen(false)}
                    currentLang={i18n.language}
                    onSelect={handleSelect}
                />
            )}

            {/* Desktop: Dropdown */}
            {!isMobile && (
                <AnimatePresence>
                    {isOpen && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="absolute right-0 mt-2 w-40 rounded-md border border-border bg-popover p-1 shadow-md z-50"
                        >
                            <div className="space-y-1">
                                {languages.map((lang) => (
                                    <button
                                        key={lang.code}
                                        onClick={() => handleSelect(lang.code)}
                                        className={`flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground ${i18n.language === lang.code ? 'bg-accent' : ''
                                            }`}
                                    >
                                        <span className="flex items-center gap-2">
                                            <span>{lang.flag}</span>
                                            <span>{lang.label}</span>
                                        </span>
                                        {i18n.language === lang.code && <Check className="h-3 w-3" />}
                                    </button>
                                ))}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            )}
        </div>
    );
}
